import React from 'react';
import { useGameStore } from '../../stores/gameStore';
import { useUserStore } from '../../stores/userStore';
import { calculateXpForLevel, formatNumber, formatDuration } from '../../shared/utils';

export function ProfileScreen() {
  const setScreen = useGameStore(s => s.setScreen);
  const user = useUserStore(s => s.user);
  const updateProfile = useUserStore(s => s.updateProfile);

  if (!user) return null;

  const xpForNext = calculateXpForLevel(user.level);
  const xpProgress = (user.xp / xpForNext) * 100;
  const kdr = user.stats.deaths > 0 ? (user.stats.kills / user.stats.deaths).toFixed(2) : String(user.stats.kills);
  const winRate = user.stats.gamesPlayed > 0
    ? ((user.stats.wins / user.stats.gamesPlayed) * 100).toFixed(1)
    : '0';

  const stats = [
    { label: 'Games Played', value: formatNumber(user.stats.gamesPlayed) },
    { label: 'Wins', value: formatNumber(user.stats.wins) },
    { label: 'Win Rate', value: `${winRate}%` },
    { label: 'Kills', value: formatNumber(user.stats.kills) },
    { label: 'Deaths', value: formatNumber(user.stats.deaths) },
    { label: 'Assists', value: formatNumber(user.stats.assists) },
    { label: 'K/D Ratio', value: kdr },
    { label: 'Headshots', value: formatNumber(user.stats.headshots) },
    { label: 'Accuracy', value: `${user.stats.accuracy}%` },
    { label: 'Play Time', value: formatDuration(user.stats.playtime) },
    { label: 'Best Streak', value: String(user.stats.bestKillStreak) },
    { label: 'Best Score', value: formatNumber(user.stats.bestScore) },
  ];

  const ranks = [
    { name: 'bronze', label: 'Bronze', color: '#cd7f32', min: 0 },
    { name: 'silver', label: 'Silver', color: '#c0c0c0', min: 500 },
    { name: 'gold', label: 'Gold', color: '#ffd700', min: 2000 },
    { name: 'platinum', label: 'Platinum', color: '#00e5ff', min: 5000 },
    { name: 'diamond', label: 'Diamond', color: '#b9f2ff', min: 10000 },
    { name: 'master', label: 'Master', color: '#7c4dff', min: 25000 },
    { name: 'legend', label: 'Legend', color: '#ff1744', min: 50000 },
  ];

  const currentRank = ranks.find(r => r.name === user.rank) || ranks[0];

  return (
    <div className="w-full h-full flex flex-col bg-cf-black overflow-y-auto">
      <div className="flex items-center justify-between px-6 py-4 border-b border-cf-border sticky top-0 bg-cf-black/90 backdrop-blur-sm z-10">
        <h2 className="text-xl font-bold text-cf-white">Profile</h2>
        <button onClick={() => setScreen('menu')} className="cf-btn-secondary text-xs">Back</button>
      </div>

      <div className="max-w-4xl w-full mx-auto p-6 space-y-6">
        <div className="cf-panel p-6">
          <div className="flex items-start gap-6">
            <div className="w-24 h-24 rounded-2xl bg-gradient-to-br from-cf-cyan to-cf-blue flex items-center justify-center text-4xl font-black text-cf-black">
              {user.displayName.charAt(0)}
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-1">
                <input
                  type="text"
                  value={user.displayName}
                  onChange={(e) => updateProfile({ displayName: e.target.value })}
                  className="bg-transparent text-2xl font-bold text-cf-white outline-none border-b border-transparent hover:border-cf-border focus:border-cf-cyan transition-colors"
                />
              </div>
              <div className="flex items-center gap-3 mb-3">
                <span className="text-sm text-cf-muted">@{user.username}</span>
                <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase" style={{ color: currentRank.color, backgroundColor: currentRank.color + '20', border: `1px solid ${currentRank.color}40` }}>
                  {currentRank.label}
                </span>
                <span className="text-sm text-cf-muted">Level {user.level}</span>
                <span className="text-sm text-cf-cyan">{user.title}</span>
              </div>
              <div className="w-full max-w-md">
                <div className="flex items-center justify-between text-xs text-cf-muted mb-1">
                  <span>Level {user.level}</span>
                  <span>{formatNumber(user.xp)} / {formatNumber(xpForNext)} XP</span>
                </div>
                <div className="h-2 bg-cf-dark rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-cf-cyan to-cf-blue rounded-full" style={{ width: `${xpProgress}%` }} />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-3">
          {stats.map((stat) => (
            <div key={stat.label} className="cf-panel p-3 text-center">
              <div className="text-lg font-black text-cf-white">{stat.value}</div>
              <div className="text-[10px] text-cf-muted uppercase tracking-wider mt-1">{stat.label}</div>
            </div>
          ))}
        </div>

        <div className="cf-panel p-6">
          <h3 className="text-sm font-bold text-cf-white mb-4 uppercase tracking-wider">Rank Progress</h3>
          <div className="flex items-center gap-2">
            {ranks.map((rank, i) => {
              const isCurrentRank = rank.name === user.rank;
              const isAchieved = user.stats.xpEarned >= rank.min;
              return (
                <div key={rank.name} className="flex-1 text-center">
                  <div
                    className={`w-10 h-10 mx-auto rounded-xl flex items-center justify-center text-lg mb-1 transition-all ${
                      isCurrentRank ? 'ring-2 ring-offset-2 ring-offset-cf-panel' : ''
                    }`}
                    style={{
                      backgroundColor: isAchieved ? rank.color + '30' : '#1a1d26',
                      borderColor: isAchieved ? rank.color : '#2a2e3d',
                      border: `2px solid ${isAchieved ? rank.color : '#2a2e3d'}`,
                    }}
                  >
                    <span className="text-sm" style={{ color: isAchieved ? rank.color : '#6b7280' }}>
                      {i + 1}
                    </span>
                  </div>
                  <div className="text-[9px] font-medium" style={{ color: isAchieved ? rank.color : '#6b7280' }}>
                    {rank.label}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
